import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  DollarSign, ExternalLink, ShieldCheck, Loader2, TrendingUp, Coins, Check,
  Gift, Landmark, FileText, Hourglass,
} from "lucide-react";

function StatCard({ label, value, hint, Icon, testid }) {
  return (
    <div className="card-surface p-4" data-testid={testid}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon size={14} className="text-rose-500" />}
      </div>
      <div className="font-display text-2xl">{value}</div>
      {hint && <div className="text-[11px] text-zinc-500 mt-1">{hint}</div>}
    </div>
  );
}

const usd = (cents) => `$${((cents || 0) / 100).toFixed(2)}`;

export const PayoutsPanel = () => {
  const [status, setStatus] = useState(null);
  const [earnings, setEarnings] = useState(null);
  const [summary, setSummary] = useState(null);
  const [tax, setTax] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/payments/creator/connect-status").then(({ data }) => setStatus(data)).catch(() => setStatus(null)),
      api.get("/payments/creator/earnings").then(({ data }) => setEarnings(data)).catch(() => setEarnings(null)),
      api.get("/payments/payouts/summary").then(({ data }) => setSummary(data)).catch(() => setSummary(null)),
      api.get("/payments/payouts/tax").then(({ data }) => setTax(data)).catch(() => setTax(null)),
    ]).finally(() => setLoading(false));
  }, []);

  const startOnboarding = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/creator/connect-onboarding", { origin_url: window.location.origin });
      if (data?.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        toast.error("Stripe didn't return an onboarding link.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Stripe onboarding.");
    } finally { setBusy(false); }
  };

  if (loading) {
    return <div className="text-sm text-zinc-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Loading payouts…</div>;
  }

  const isReady = !!status?.onboarded;
  const isPartial = !!status?.connected && !status?.onboarded;
  const feePct = earnings?.platform_fee_pct ?? 45;
  const memberPct = 100 - feePct;
  const bal = summary?.balance;

  return (
    <div className="space-y-6" data-testid="earnings-payouts-panel">
      {/* Status hero */}
      <div className={`card-surface p-5 ${isReady ? "border-emerald-500/40" : "border-amber-500/40"}`}>
        <div className="flex items-start gap-4">
          <div className={`w-11 h-11 rounded-full grid place-items-center shrink-0 ${
            isReady ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"
          }`}>
            {isReady ? <ShieldCheck size={22} /> : <DollarSign size={22} />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs uppercase tracking-[0.28em] mb-1 text-zinc-400">
              {isReady ? "Payouts active" : "Payouts"}
            </div>
            <div className="font-display text-2xl leading-tight">
              {isReady ? "You're all set — money flows automatically."
                : isPartial ? "Almost there — finish Stripe verification"
                : "Turn on payouts to get paid"}
            </div>
            <div className="text-sm text-zinc-400 mt-1 leading-relaxed">
              {isReady
                ? `Every tip and shop purchase is deposited to your bank on Stripe's rolling schedule. Platform fee is ${feePct}%.`
                : "Stripe handles ID + banking so we don't touch it. Onboarding takes about 3 minutes."}
            </div>
            {!isReady && (
              <Button
                onClick={startOnboarding}
                disabled={busy}
                className="btn-crimson rounded-full mt-4 h-10 px-5"
                data-testid="payouts-onboarding-btn"
              >
                {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <ExternalLink size={14} className="mr-2" />}
                {isPartial ? "Continue Stripe verification" : "Turn on payouts"}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Pending vs available balance */}
      {status?.connected && (
        <div className="grid grid-cols-2 gap-3">
          <StatCard label="Available balance" value={bal ? usd(bal.available_cents) : "—"}
            hint="Cleared and queued for your next automatic payout" Icon={Landmark} testid="payouts-available-balance" />
          <StatCard label="Pending balance" value={bal ? usd(bal.pending_cents) : "—"}
            hint="Recent earnings still clearing — not withdrawable yet" Icon={Hourglass} testid="payouts-pending-balance" />
        </div>
      )}

      {/* Earnings summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Net earnings" value={usd(earnings?.net_cents)} hint={`Your ${memberPct}% share`} Icon={DollarSign} />
        <StatCard label="Gross received" value={usd(earnings?.gross_cents)} hint="Total tips + shop" Icon={TrendingUp} />
        <StatCard label="Platform fee" value={`${feePct}%`} hint="Kept by My Panda X" Icon={Coins} />
        <StatCard label="Payments" value={(earnings?.count || 0).toLocaleString()} hint="Successful charges" Icon={Check} />
      </div>

      {/* Payout history */}
      {status?.connected && (
        <div className="card-surface p-4">
          <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
            <Landmark size={12} /> Payout history
          </div>
          {(summary?.payouts || []).length === 0 ? (
            <div className="text-xs text-zinc-500 py-4 text-center" data-testid="payouts-history-empty">
              No bank payouts yet. Once funds clear, Stripe deposits them automatically.
            </div>
          ) : (
            <div className="space-y-2" data-testid="payouts-history">
              {summary.payouts.map((p) => (
                <div key={p.id} className="flex items-center justify-between text-sm border-b border-white/5 last:border-0 py-2">
                  <div className="text-white">{usd(p.amount_cents)}</div>
                  <div className="text-right">
                    <div className={p.status === "paid" ? "text-emerald-300" : "text-amber-300"}>{p.status}</div>
                    <div className="text-[10px] text-zinc-500">
                      {p.arrival_date ? new Date(p.arrival_date * 1000).toLocaleDateString() : ""}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Recent tips */}
      <div className="card-surface p-4">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
          <Gift size={12} /> Recent tips
        </div>
        {(earnings?.recent || []).length === 0 ? (
          <div className="text-xs text-zinc-500 py-4 text-center">
            {isReady
              ? "No tips or shop sales yet. Once fans pay, they show up here in real time."
              : "Turn on payouts first — then every tip lands here."}
          </div>
        ) : (
          <div className="space-y-2" data-testid="payouts-recent">
            {(earnings.recent || []).map((t, i) => (
              <div key={t.id || `${t.created_at}-${i}`} className="flex items-center justify-between text-sm border-b border-white/5 last:border-0 py-2">
                <div>
                  <div className="text-white">${((t.amount_cents || 0) / 100).toFixed(2)}</div>
                  {t.message && <div className="text-[11px] text-zinc-500 truncate max-w-[220px]">"{t.message}"</div>}
                </div>
                <div className="text-right">
                  <div className="text-emerald-300">+${((t.creator_net_cents || 0) / 100).toFixed(2)} to you</div>
                  <div className="text-[10px] text-zinc-500">
                    {new Date(t.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Tax reporting */}
      <div className="card-surface p-4" data-testid="payouts-tax-card">
        <div className="text-xs uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-2">
          <FileText size={12} /> Tax reporting
        </div>
        {(tax?.years || []).length === 0 ? (
          <div className="text-xs text-zinc-500 py-2">No reportable earnings yet.</div>
        ) : (
          <div className="space-y-2">
            {tax.years.map((y) => (
              <div key={y.year} className="flex items-center justify-between text-sm border-b border-white/5 last:border-0 py-2">
                <div className="text-white">{y.year}</div>
                <div className="text-right">
                  <div className="text-white">{usd(y.gross_cents)} gross</div>
                  <div className="text-[10px] text-zinc-500">{usd(y.net_cents)} net · {y.payments} payments</div>
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="text-[11px] text-zinc-500 mt-3 leading-relaxed">{tax?.note}</div>
      </div>
    </div>
  );
};

export default PayoutsPanel;
